/**
 * Elavate promo countdown.
 *
 * Clones the clock that sits above the nav on elavate.com's product pages, but
 * keeps the deadline in localStorage so it counts down across page loads
 * instead of restarting on every navigation. When a cycle runs out the clock
 * hides itself, waits, then starts a fresh cycle.
 *
 * Implemented as a custom element so the theme editor's section reloads clean
 * up their own interval instead of leaving one ticking per reload.
 */
(function () {
  'use strict';

  /* How long the clock stays hidden between cycles. */
  var RESTART_DELAY_MS = 60 * 1000;
  var FALLBACK_CYCLE_MS = 60 * 60 * 1000;

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function readNumber(value) {
    var parsed = parseInt(value, 10);
    return isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  class ElavateCountdown extends HTMLElement {
    connectedCallback() {
      this.hoursEl = this.querySelector('[data-el-hours]');
      this.minutesEl = this.querySelector('[data-el-minutes]');
      this.secondsEl = this.querySelector('[data-el-seconds]');

      if (!this.hoursEl || !this.minutesEl || !this.secondsEl) return;

      this.deadline = this.readDeadline() || Date.now() + this.cycleLength();
      this.writeDeadline(this.deadline);
      this.run();
    }

    disconnectedCallback() {
      this.stop();
    }

    /* Duration of one countdown cycle, from the section settings. */
    cycleLength() {
      var total =
        (readNumber(this.dataset.hours) * 3600 +
          readNumber(this.dataset.minutes) * 60 +
          readNumber(this.dataset.seconds)) *
        1000;

      return total > 0 ? total : FALLBACK_CYCLE_MS;
    }

    /* localStorage throws in some privacy modes — a missing deadline just
       means this visitor starts a fresh cycle. */
    readDeadline() {
      var stored = null;

      try {
        stored = window.localStorage.getItem(this.dataset.storageKey);
      } catch (error) {
        return null;
      }

      var parsed = parseInt(stored, 10);
      return isFinite(parsed) && parsed > Date.now() ? parsed : null;
    }

    writeDeadline(value) {
      try {
        window.localStorage.setItem(this.dataset.storageKey, String(value));
      } catch (error) {
        /* Nothing to persist to; the clock still runs for this page view. */
      }
    }

    run() {
      this.render();
      this.timer = window.setInterval(this.render.bind(this), 1000);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      if (this.restartTimer) {
        window.clearTimeout(this.restartTimer);
        this.restartTimer = null;
      }
    }

    render() {
      var remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        this.expire();
        return;
      }

      var seconds = Math.floor(remaining / 1000);

      this.hoursEl.textContent = pad(Math.floor(seconds / 3600));
      this.minutesEl.textContent = pad(Math.floor(seconds / 60) % 60);
      this.secondsEl.textContent = pad(seconds % 60);
    }

    expire() {
      this.stop();
      this.hidden = true;

      this.restartTimer = window.setTimeout(
        function () {
          this.deadline = Date.now() + this.cycleLength();
          this.writeDeadline(this.deadline);
          this.hidden = false;
          this.run();
        }.bind(this),
        RESTART_DELAY_MS
      );
    }
  }

  if (!window.customElements.get('el-countdown')) {
    window.customElements.define('el-countdown', ElavateCountdown);
  }
})();
